import { create } from "@bufbuild/protobuf";
import { CamoInfoSchema } from "../../../api/types";
import type {
  AttachmentId,
  CamoId,
  CamoInfo,
  FullMe,
  FullWeaponInfo,
  IdList,
  WeaponId,
  WeaponLoadout,
} from "../../../api/types";
import type { MetaIndex } from "../../../state/meta";
import { AttachmentPicker } from "./AttachmentPicker";
import { CamoPicker } from "./CamoPicker";
import { MAX_ATTACHMENTS, NO_CAMO } from "./draft";
import { Counter, Section } from "./Section";
import { WeaponGrid } from "./WeaponGrid";

/** Shown when the catalog does not list the always-available "no camo" entry itself. */
const NO_CAMO_INFO = create(CamoInfoSchema, {
  id: NO_CAMO,
  name: "None",
  weaponLevel: 1,
  colors: ["#c9c7c2", "#8d8c88"],
});

export interface WeaponSectionProps {
  title: string;
  weapons: readonly FullWeaponInfo[];
  camos: readonly CamoInfo[];
  choice: WeaponLoadout;
  account: FullMe;
  index: MetaIndex;
  onWeapon: (id: WeaponId) => void;
  onAttachment: (id: AttachmentId) => void;
  onCamo: (id: CamoId) => void;
}

/** One weapon slot: weapon cards, then attachments and camo of the selected weapon. */
export function WeaponSection({
  title,
  weapons,
  camos,
  choice,
  account,
  index,
  onWeapon,
  onAttachment,
  onCamo,
}: WeaponSectionProps) {
  const weapon = index.weapons.get(choice.weapon);
  const weaponLevel = account.weaponProgress.find((p) => p.weapon === choice.weapon)?.level ?? 1;
  const unlockedAttachments: IdList | undefined = account.unlocks.attachments[choice.weapon];
  const unlockedCamos: IdList | undefined = account.unlocks.camos[choice.weapon];
  const camoList = camos.some((c) => c.id === NO_CAMO) ? camos : [NO_CAMO_INFO, ...camos];

  return (
    <Section
      title={title}
      aside={weapon && <span class="loadouts__count">{weapon.name}</span>}
    >
      <WeaponGrid
        label={title}
        weapons={weapons}
        selected={choice.weapon}
        unlocked={account.unlocks.weapons}
        onSelect={onWeapon}
      />
      {weapon && (
        <div class="loadouts__kit">
          <div class="loadouts__kit-head">
            <h3 class="loadouts__sub">Attachments</h3>
            <Counter value={choice.attachments.length} max={MAX_ATTACHMENTS} />
          </div>
          <AttachmentPicker
            key={weapon.id}
            weapon={weapon}
            chosen={choice.attachments}
            unlocked={unlockedAttachments?.ids ?? []}
            weaponLevel={weaponLevel}
            catalog={index.attachments}
            onToggle={onAttachment}
          />
          <div class="loadouts__kit-head">
            <h3 class="loadouts__sub">Camo</h3>
            <Counter label="Weapon level" value={weaponLevel} />
          </div>
          <CamoPicker
            label={`${title} camo`}
            camos={camoList}
            selected={choice.camo}
            unlocked={unlockedCamos?.ids ?? []}
            weaponLevel={weaponLevel}
            onSelect={onCamo}
          />
        </div>
      )}
    </Section>
  );
}
